import { useEffect, useState } from 'react';
import { HOME_HERO } from '../../../data/site';
import '../../../styles/home-hero.css';

/** Orbit slots - center → right → far right → far left → left → center */
const SLOT_NAMES = ['center', 'rightMid', 'rightFar', 'leftFar', 'leftMid'] as const;
type SlotName = (typeof SLOT_NAMES)[number];

const ORBS = [
  { id: 'main', image: HOME_HERO.image, alt: HOME_HERO.imageAlt },
  { id: 'left-mid', ...HOME_HERO.flanks.left[0] },
  { id: 'left-far', ...HOME_HERO.flanks.left[1] },
  { id: 'right-mid', ...HOME_HERO.flanks.right[0] },
  { id: 'right-far', ...HOME_HERO.flanks.right[1] },
] as const;

/** Which slot each orb starts in */
const INITIAL_POSITIONS = [0, 4, 3, 1, 2];

const ORBIT_ANIMATION_ENABLED = true;
const ORBIT_INTERVAL_MS = 6_500;

function ringDepth(slot: SlotName): 'center' | 'mid' | 'far' {
  if (slot === 'center') return 'center';
  if (slot === 'leftMid' || slot === 'rightMid') return 'mid';
  return 'far';
}

function nextPositions(prev: number[]) {
  return prev.map((slot) => (slot + 1) % SLOT_NAMES.length);
}

export function HeroSection() {
  const [positions, setPositions] = useState(INITIAL_POSITIONS);

  useEffect(() => {
    if (!ORBIT_ANIMATION_ENABLED) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const interval = window.setInterval(() => {
      setPositions((prev) => nextPositions(prev));
    }, ORBIT_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className="home-hero home-hero--orbit">
      <div className="home-hero__aura" aria-hidden />

      <div className="container home-hero__inner">
        <p className="home-hero__kicker">{HOME_HERO.kicker}</p>
        <h1 className="home-hero__title">{HOME_HERO.title}</h1>

        <div className="home-hero__stage">
          <div className="home-hero__depth">
            {ORBS.map((orb, index) => {
              const slot = SLOT_NAMES[positions[index]];
              return (
                <div
                  key={orb.id}
                  className={`home-hero__orb-unit home-hero__orb-unit--${slot}`}
                >
                  <div
                    className={`home-hero__ring home-hero__ring--${ringDepth(slot)}`}
                    aria-hidden
                  />
                  <figure className="home-hero__orb">
                    <img
                      src={orb.image}
                      alt={orb.alt}
                      className="home-hero__image"
                      loading="eager"
                      decoding="async"
                    />
                  </figure>
                </div>
              );
            })}
          </div>
        </div>

        <p className="home-hero__subtitle">{HOME_HERO.subtitle}</p>
      </div>
    </section>
  );
}
